// =============================================================================
// APP VARIANT OVERRIDES
// Organized by component. Each section extends shadcn defaults with app styling.
// =============================================================================

type VariantOverride = {
  pattern: RegExp;
  append: string;
};

// =============================================================================
// BUTTON
// =============================================================================
// Note: Button uses cva() not cn(), so it doesn't have shadcn-button identifier
// Keep existing patterns for button variants
const buttonOverrides: Record<string, VariantOverride> = {
  // All buttons: default height h-9, no focus ring
  base: {
    pattern: /\binline-flex\b.*\bgap-2\b/,
    append: 'focus-visible:!ring-0',
  },
  // Default variant: override disabled to use muted instead of opacity
  default: {
    pattern: /\bbg-primary\b.*\btext-primary-foreground\b/,
    append: 'disabled:!opacity-100 disabled:!bg-muted disabled:!text-muted-foreground/50',
  },
  outline: {
    pattern: /\bborder\b.*\bbg-background\b/,
    append: 'data-[state=active]:text-brand-foreground data-[state=active]:bg-brand-foreground/10 rounded-lg text-muted-foreground relative overflow-hidden after:absolute after:inset-0 after:rounded-[inherit] after:bg-brand-foreground/20 after:scale-0 after:origin-bottom-right after:pointer-events-none [&.ripple-active]:after:scale-100 [&.ripple-active]:after:animate-ripple',
  },
  ghost: {
    pattern: /\bhover:bg-accent\b.*\bhover:text-accent-foreground\b/,
    append: 'rounded-lg text-muted-foreground',
  },
  secondary: {
    pattern: /\bhover:bg-secondary\b.*\bhover:text-secondary-foreground\b/,
    append: 'rounded-lg',
  },
};

// =============================================================================
// TABS (match outline button variant)
// =============================================================================
const tabsOverrides: Record<string, VariantOverride> = {
  list: {
    // Match using the shadcn-tabs identifier class
    pattern: /\bshadcn-tabs\b/,
    append: '',
  },
  trigger: {
    // Match using the shadcn-tabstrigger identifier class
    pattern: /\bshadcn-tabstrigger\b/,
    //append: '',
    append: 'data-[state=active]:dark:text-brand-foreground data-[state=active]:dark:bg-brand-foreground/10 data-[state=active]:bg-mainbackground data-[state=active]:text-brand-foreground text-muted-foreground hover:text-foreground dark:hover:text-foreground',
  },
};

// =============================================================================
// CARD
// =============================================================================
const cardOverrides: Record<string, VariantOverride> = {
  card: {
    pattern: /\bshadcn-card\b/,
    append: 'dark:bg-card/30 dark:border-none shadow-none',
  },
  header: {
    pattern: /\bshadcn-cardheader\b/,
    append: 'dark:bg-transparent',
  },
};

// =============================================================================
// RADIO GROUP
// =============================================================================
const radioOverrides: Record<string, VariantOverride> = {
  item: {
    pattern: /\bshadcn-radiogroupitem\b/,
    append: '[&_svg]:!fill-brand data-[state=checked]:border-brand/60 focus-visible:!ring-0 focus-visible:!border-brand',
  },
};

// =============================================================================
// TOGGLE (uses data-[state=on] for selected state, aria-checked for radio groups)
// =============================================================================
// Note: Toggle uses cva() not cn(), so it doesn't have shadcn-toggle identifier
// But ToggleGroup does use cn() and has shadcn-togglegroup identifier
// ToggleGroupItems wrapped in tooltips use aria-checked instead of data-state
const toggleOverrides: Record<string, VariantOverride> = {
  toggleGroup: {
    // Match using the shadcn-togglegroup identifier class
    pattern: /\bshadcn-togglegroup\b/,
    append: 'data-[state=on]:bg-brand-foreground/10 data-[state=on]:text-brand-foreground dark:data-[state=on]:bg-brand-foreground/10 dark:data-[state=on]:text-brand-foreground [&_[aria-checked=true]]:bg-brand-foreground/10 [&_[aria-checked=true]]:text-brand-foreground',
  },
  // For Toggle component (uses cva), override both data-state and aria-checked
  toggle: {
    pattern: /\baria-\[checked=true\]:bg-accent\b.*\baria-\[checked=true\]:text-accent-foreground\b/,
    append: 'aria-[checked=true]:bg-brand-foreground/10 aria-[checked=true]:text-brand-foreground dark:aria-[checked=true]:bg-brand-foreground/10 dark:aria-[checked=true]:text-brand-foreground data-[state=on]:bg-brand-foreground/10 data-[state=on]:text-brand-foreground dark:data-[state=on]:bg-brand-foreground/10 dark:data-[state=on]:text-brand-foreground',
  },
};

// =============================================================================
// INPUT
// =============================================================================
const inputOverrides: Record<string, VariantOverride> = {
  default: {
    // Match using the shadcn-input identifier class (added by patch-shadcn-primitives script)
    pattern: /\bshadcn-input\b/,
    append: 'focus-visible:!ring-0 focus-visible:!border-brand-foreground/80',
  },
};

// =============================================================================
// TEXTAREA OVERRIDES
// Match Textarea focus styling to Input (no ring, just border color change)
// =============================================================================
const textareaOverrides: Record<string, VariantOverride> = {
  default: {
    pattern: /\bshadcn-textarea\b/,
    append: 'focus-visible:!ring-0 focus-visible:!border-brand-foreground/80',
  },
};

// =============================================================================
// COLOR TRANSITIONS
// Auto-add transition-colors to elements using color utilities for smooth theme switching
// =============================================================================
const colorTransitionOverrides: Record<string, VariantOverride> = {
  background: {
    pattern: /\bbg-(?!transparent|inherit|current|none)/,
    append: 'transition-colors duration-200',
  },
  text: {
    pattern: /\btext-(?!left|right|center|justify|wrap|nowrap|ellipsis|clip|xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)/,
    append: 'transition-colors duration-200',
  },
  border: {
    pattern: /\bborder-(?!0|2|4|8|x|y|t|r|b|l|solid|dashed|dotted|double|hidden|none|collapse|separate)/,
    append: 'transition-colors duration-200',
  },
  fill: {
    pattern: /\bfill-/,
    append: 'transition-colors duration-200',
  },
  stroke: {
    pattern: /\bstroke-(?!0|1|2)/,
    append: 'transition-colors duration-200',
  },
};

// =============================================================================
// COMBINED OVERRIDES
// =============================================================================
export const appVariantOverrides: VariantOverride[] = [
  ...Object.values(buttonOverrides),
  ...Object.values(tabsOverrides),
  ...Object.values(cardOverrides),
  ...Object.values(radioOverrides),
  ...Object.values(toggleOverrides),
  ...Object.values(inputOverrides),
  ...Object.values(textareaOverrides),
  ...Object.values(colorTransitionOverrides),
];
